/**
 * Opt-in forced reload for clients running an old build.
 *
 * Every bundle carries `__BUILD_TIME__` (ms, stamped by the vite config). To force
 * old clients (web + the Cordova shells, where a reload IS the update) onto a fix:
 * after the fix is deployed, set `appConfig/minBuildTime` in the Firebase console (RTDB)
 * to the current time, as ms (`1791300000000`) or ISO (`2026-10-05T18:00:00-05:00`).
 * Optionally set `appConfig/reason`. Clients built before that reload at their next
 * safe moment (see useForceReload.ts), or show a "Recargar" banner until then;
 * builds made after it ignore it. No deploy
 * needed, and a plain deploy never reloads anyone.
 */

export interface ForceReloadConfig {
  minBuildTime: number
  reason: string
}

/** ms since epoch from a number, a digits-only string, or an ISO date string. */
function toEpochMs(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (!text) return null
  const ms = /^\d+$/.test(text) ? Number(text) : Date.parse(text)
  return Number.isFinite(ms) ? ms : null
}

/** Read the `appConfig` node defensively; anything malformed means "no force". */
export function parseForceReloadConfig(raw: unknown): ForceReloadConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const { minBuildTime, reason } = raw as Record<string, unknown>
  const ms = toEpochMs(minBuildTime)
  if (ms == null || ms <= 0) return null
  return { minBuildTime: ms, reason: typeof reason === 'string' ? reason : '' }
}

/**
 * True when this client must reload: its build predates `minBuildTime` and it has not
 * already reloaded for this same `minBuildTime` in this tab (guards against a loop when
 * a stale index.html keeps serving the old bundle). A build id of 0 means unknown
 * (vitest, unstamped dev) and never reloads.
 */
export function shouldForceReload(
  buildTime: number,
  minBuildTime: number,
  alreadyActedOn: number | null,
): boolean {
  if (!Number.isFinite(buildTime) || buildTime <= 0) return false
  if (!Number.isFinite(minBuildTime) || minBuildTime <= 0) return false
  if (buildTime >= minBuildTime) return false
  return alreadyActedOn !== minBuildTime
}

/** Driver stream views: never reload or interrupt them (tracking + live trip state). */
export const STREAM_PATH = /^\/staff\/(route|trip)\//

export function isStreamPath(path: string): boolean {
  return STREAM_PATH.test(path)
}

/**
 * The update banner shows while this build is stale and was not auto-reloaded, unless
 * the user closed it (until the next return to the app), the view is a driver stream,
 * or native tracking is on.
 */
export function shouldShowUpdateBanner(state: {
  updatePending: boolean
  dismissed: boolean
  path: string
  tracking: boolean
}): boolean {
  if (!state.updatePending || state.dismissed || state.tracking) return false
  return !isStreamPath(state.path)
}
